// Header.jsx - Updated header with reduced mobile height and Pricing Calculator

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/thunks/authThunks";
import { getCompanyDetailsThunk } from "../../store/thunks/companyDetailsThunks";
import {
  LogoutIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon,
  XIcon,
  TranslateIcon,
  UserCircleIcon,
  CalculatorIcon,
} from "@heroicons/react/outline";
import HeaderBranchSelector from "../UI/HeaderBranchSelector";
import Button from "../common/Button";
import appConfig from "config/appConfig";
import { Dialog, DialogPanel, Transition } from "@headlessui/react";
import { useI18n } from "../../hooks/useI18n";
import {
  getFilteredMenuConfig,
  getFilteredQuickAccessItems,
  DEFAULT_FEATURES_CONFIG,
} from "../../config/menuConfig";
import HeaderRateWidget from "../UI/HeaderRateWidget";
import HeaderAvailabilityWidget from "../UI/HeaderAvailabilityWidget";
import PricingCalculator from "../../pages/PricingRules/components/PricingCalculator";

const Header = ({ menuConfig, quickAccessItems, featuresConfig }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [companyName, setCompanyName] = useState(appConfig.APP_NAME);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showCalculator, setShowCalculator] = useState(false);

  // Get i18n through our custom hook
  const { t, language, changeLanguage, languages } = useI18n();

  // Merge feature configs with defaults
  const features = { ...DEFAULT_FEATURES_CONFIG, ...featuresConfig };

  const dispatch = useDispatch();
  const location = useLocation();
  const { companyDetails } = useSelector((state) => state.companyDetails);
  const { isRootAdmin, permissions = [] } = useSelector((state) => state.auth);

  // Use filtered menu config if not provided explicitly
  const filteredMenuConfig =
    menuConfig || getFilteredMenuConfig(permissions, isRootAdmin, features);

  const filteredQuickAccess =
    quickAccessItems ||
    getFilteredQuickAccessItems(permissions, isRootAdmin, features);

  useEffect(() => {
    dispatch(getCompanyDetailsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (companyDetails) {
      setCompanyName(companyDetails.companyName || appConfig.APP_NAME);
      setCompanyLogo(companyDetails.logoUrl || null);
    }
  }, [companyDetails]);

  // Initialize expanded groups
  useEffect(() => {
    const initialExpanded = {};
    filteredMenuConfig?.forEach((group) => {
      initialExpanded[group.id] = false; // Start collapsed on mobile
    });
    setExpandedGroups(initialExpanded);
  }, [filteredMenuConfig]);

  const openMenu = () => setIsMenuOpen(true);
  const closeMenu = () => {
    setIsMenuOpen(false);
    setSearchQuery(""); // Clear search on close
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm(t("header.logout_confirm"));
    if (confirmLogout) {
      dispatch(logout());
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  const isGroupActive = (group) => {
    return group.items.some((item) => location.pathname === item.path);
  };

  // Toggle language
  const handleToggleLanguage = () => {
    const nextLang = language === "en" ? "hi" : "en";
    changeLanguage(nextLang);
  };

  // Filter menu items based on search
  const getFilteredGroups = () => {
    if (!searchQuery || !filteredMenuConfig) return filteredMenuConfig || [];

    const query = searchQuery.toLowerCase();
    return filteredMenuConfig
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const itemName = t(item.translationKey).toLowerCase();
          return itemName.includes(query);
        }),
      }))
      .filter((group) => group.items.length > 0);
  };

  // Calculator Button Component for reusability
  const CalculatorButton = ({ variant = "desktop", onClick }) => {
    if (variant === "desktop") {
      return (
        <button
          onClick={onClick}
          className="group h-fill relative flex items-center gap-2 px-3 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-lg transition-all duration-200 border border-white border-opacity-20 hover:border-opacity-30"
          title={t("pricing.calculator") || "Pricing Calculator"}
        >
          <CalculatorIcon className="w-5 h-5 text-text-on-primary" />
          <span className="hidden lg:inline text-sm font-medium text-text-on-primary">
            {t("pricing.calculator") || "Calculator"}
          </span>
        </button>
      );
    }

    // Mobile variant
    return (
      <button
        onClick={onClick}
        className="flex items-center w-full px-4 py-3 text-text-on-primary text-opacity-80 hover:bg-primary-dark hover:bg-opacity-20 transition-colors"
      >
        <CalculatorIcon className="w-5 h-5 mr-3" />
        <span>{t("pricing.calculator") || "Pricing Calculator"}</span>
      </button>
    );
  };

  return (
    <>
      {/* Header - Compact on mobile, normal on desktop */}
      <header
        className="
          relative bg-primary text-text-on-primary px-3 flex justify-between items-center w-full shadow-lg
          py-2 md:py-3
        "
      >
        <div className="flex items-center space-x-2 md:space-x-3 flex-1">
          {/* Mobile Menu Button - Hidden since we have bottom nav now */}
          <button
            onClick={openMenu}
            className="focus:outline-none md:hidden p-1.5 hover:bg-primary-dark hover:bg-opacity-30 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5 md:w-6 md:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </button>

          {/* Company Logo - Smaller on mobile */}
          {companyLogo && (
            <div className="flex items-center">
              <img
                src={companyLogo}
                alt="Company Logo"
                className="h-8 w-8 md:h-10 md:w-10 object-contain rounded-lg"
              />
            </div>
          )}

          {/* Branch Selector - Desktop only, on mobile it's in the menu */}
          <div className="hidden md:flex items-center ml-auto mr-4">
            <HeaderBranchSelector />
          </div>
        </div>

        {/* Right Side Actions - More compact on mobile */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3">
          {/* Calculator Button - Desktop */}
          <div className="hidden sm:block">
            <CalculatorButton
              variant="desktop"
              onClick={() => setShowCalculator(true)}
            />
          </div>

          {/* Calculator Button - Mobile (Icon only, smaller) */}
          <button
            onClick={() => setShowCalculator(true)}
            className="sm:hidden p-1.5 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-lg transition-all duration-200 border border-white border-opacity-20"
            title={t("pricing.calculator") || "Calculator"}
          >
            <CalculatorIcon className="w-4 h-4 md:w-5 md:h-5 text-text-on-primary" />
          </button>

          {/* Rate Widget - Compact version for mobile */}
          <div className="scale-90 md:scale-100 origin-right">
            <HeaderRateWidget compact={true} />
          </div>

          {/* Availability Widget - Hide on very small screens */}
          {/* <div className="hidden xs:block"> */}
          <HeaderAvailabilityWidget />
          {/* </div> */}
        </div>
      </header>

      {/* Pricing Calculator Modal */}
      {showCalculator && (
        <PricingCalculator onClose={() => setShowCalculator(false)} />
      )}

      {/* Mobile Menu Dialog - Keep for additional menu access */}
      <Transition show={isMenuOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-50 md:hidden"
          onClose={closeMenu}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={React.Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative flex w-full max-w-xs flex-col bg-primary overflow-hidden">
                {/* Menu Header - Compact */}
                <div className="flex items-center justify-between p-3 border-b border-primary-dark border-opacity-30">
                  <div className="flex items-center space-x-2">
                    {companyLogo && (
                      <img
                        src={companyLogo}
                        alt="Logo"
                        className="h-7 w-7 object-contain rounded"
                      />
                    )}
                    <h2 className="text-base font-bold text-text-on-primary truncate max-w-[150px]">
                      {companyName}
                    </h2>
                  </div>
                  <button
                    onClick={closeMenu}
                    className="p-1.5 hover:bg-primary-dark hover:bg-opacity-30 rounded-lg transition-colors"
                  >
                    <XIcon className="w-5 h-5 text-text-on-primary" />
                  </button>
                </div>

                {/* Branch Selector - Mobile */}
                <div className="p-2.5 border-b border-primary-dark border-opacity-30">
                  <HeaderBranchSelector />
                </div>

                {/* Calculator Quick Access - Mobile Menu */}
                <div className="border-b border-primary-dark border-opacity-30">
                  <CalculatorButton
                    variant="mobile"
                    onClick={() => {
                      closeMenu();
                      setShowCalculator(true);
                    }}
                  />
                </div>

                {/* Language Toggle */}
                {features.languageToggle && (
                  <div className="p-2 border-b border-primary-dark border-opacity-30 flex justify-end">
                    <button
                      onClick={handleToggleLanguage}
                      className="flex items-center px-3 py-1 text-sm bg-white bg-opacity-20 rounded-lg"
                    >
                      <TranslateIcon className="w-4 h-4 mr-1" />
                      <span>{language === "en" ? "Hindi" : "English"}</span>
                    </button>
                  </div>
                )}

                {/* Search Bar */}
                {features.searchBar && (
                  <div className="p-3 border-b border-primary-dark border-opacity-30">
                    <div className="relative">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-on-primary text-opacity-50" />
                      <input
                        type="text"
                        placeholder={t("common.search_menu")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-primary-dark bg-opacity-30 rounded-lg text-text-on-primary placeholder-text-on-primary placeholder-opacity-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light"
                      />
                    </div>
                  </div>
                )}

                {/* Quick Access Items */}
                {features.quickAccess &&
                  filteredQuickAccess &&
                  filteredQuickAccess.length > 0 && (
                    <div className="border-b border-primary-dark border-opacity-30">
                      <div className="px-3 py-1.5">
                        <span className="text-xs uppercase tracking-wider text-text-on-primary text-opacity-60">
                          {t("common.menu")}
                        </span>
                      </div>
                      {filteredQuickAccess.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={closeMenu}
                          className={`
                          flex items-center px-4 py-2.5 transition-colors
                          ${
                            isActiveLink(item.path)
                              ? "bg-primary-dark bg-opacity-30 text-text-on-primary font-medium"
                              : "text-text-on-primary text-opacity-80 hover:bg-primary-dark hover:bg-opacity-20"
                          }
                        `}
                        >
                          <item.icon className="w-5 h-5 mr-3" />
                          <span className="text-sm">
                            {t(item.translationKey)}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}

                {/* Main Navigation */}
                <nav className="flex-1 overflow-y-auto">
                  {getFilteredGroups().map((group) => (
                    <div
                      key={group.id}
                      className="border-b border-primary-dark border-opacity-20"
                    >
                      {features.groupHeaders && (
                        <button
                          onClick={() => toggleGroup(group.id)}
                          className={`
                            w-full flex items-center justify-between px-4 py-2.5
                            transition-colors
                            ${
                              isGroupActive(group)
                                ? "bg-primary-dark bg-opacity-20 text-text-on-primary"
                                : "text-text-on-primary text-opacity-80 hover:bg-primary-dark hover:bg-opacity-10"
                            }
                          `}
                        >
                          <div className="flex items-center">
                            <group.icon className="w-5 h-5 mr-3" />
                            <span className="font-medium text-sm">
                              {t(`${group.translationKey}.label`)}
                            </span>
                            {features.groupBadges && group.badge && (
                              <span className="ml-2 bg-danger text-white text-xs rounded-full px-2 py-0.5">
                                {group.badge.count}
                              </span>
                            )}
                          </div>
                          {expandedGroups[group.id] ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}

                      <Transition
                        show={
                          !features.groupHeaders || expandedGroups[group.id]
                        }
                        enter="transition-all duration-200 ease-out"
                        enterFrom="opacity-0 max-h-0"
                        enterTo="opacity-100 max-h-96"
                        leave="transition-all duration-200 ease-in"
                        leaveFrom="opacity-100 max-h-96"
                        leaveTo="opacity-0 max-h-0"
                      >
                        <div className="bg-primary-dark bg-opacity-10">
                          {group.items.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={closeMenu}
                              className={`
                                flex items-center ${
                                  features.groupHeaders ? "pl-11" : "pl-4"
                                } pr-4 py-2 transition-colors
                                ${
                                  isActiveLink(item.path)
                                    ? "bg-primary-dark bg-opacity-20 text-text-on-primary font-medium"
                                    : "text-text-on-primary text-opacity-70 hover:bg-primary-dark hover:bg-opacity-15 hover:text-opacity-100"
                                }
                              `}
                            >
                              <item.icon className="w-4 h-4 mr-3" />
                              <span className="text-sm">
                                {t(item.translationKey)}
                              </span>
                              {features.groupBadges && item.badge && (
                                <span className="ml-auto bg-danger text-white text-xs rounded-full px-1.5 py-0.5">
                                  {item.badge.count}
                                </span>
                              )}
                            </Link>
                          ))}
                        </div>
                      </Transition>
                    </div>
                  ))}
                </nav>

                {/* Profile & Logout Section - Fixed at Bottom, Compact */}
                <div className="p-3 border-t border-primary-dark border-opacity-30 bg-primary-dark bg-opacity-30">
                  <div className="flex gap-2 items-center">
                    {/* Logout Button */}
                    <button
                      className="
                        group flex items-center justify-center transition-all duration-200
                        bg-danger hover:bg-danger-dark active:bg-danger-dark text-white
                        border border-danger-dark
                        shadow-md hover:shadow-lg active:shadow-sm
                        p-2 rounded-lg
                      "
                      onClick={handleLogout}
                      title="Logout"
                    >
                      <LogoutIcon className="w-4 h-4 flex-shrink-0 transition-transform group-hover:scale-110" />
                    </button>

                    {/* Profile Button */}
                    <Link
                      to="/profile"
                      onClick={closeMenu}
                      className={`
                        group flex items-center justify-center transition-all duration-200
                        bg-white bg-opacity-10 hover:bg-opacity-20 text-text-on-primary
                        border border-white border-opacity-20 hover:border-opacity-30
                        py-2 px-4 rounded-lg gap-2 flex-1
                        ${
                          isActiveLink("/profile")
                            ? "bg-opacity-25 border-opacity-40 shadow-lg"
                            : "hover:shadow-md"
                        }
                      `}
                    >
                      <UserCircleIcon className="w-4 h-4 flex-shrink-0 transition-transform group-hover:scale-110" />
                      <span className="text-sm font-semibold tracking-wide">
                        {t("common.profile")}
                      </span>
                    </Link>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Header;
